/* SUGARCOAT — catalog behaviour (collection + search).
   Progressive enhancement over plain GET forms / pagination links.
   Hooks:
   - <catalog-root data-section-id>     wraps the section; moves the filter drawer + overlay to <body>
                                        (so they escape main's stacking context)
   - form[data-catalog-form]            filter form: submits via fetch, pushState
   - select[data-sort]                  sort select (uses form="…" attr), submits the filter form
   - a[data-filter-link]                pill remove / clear-all links -> fetched in place
   - [data-swap="name"]                 regions replaced from the fetched page (matched by name + index)
   - [data-append="name"]               containers that receive appended children on "Load more"
   - a[data-load-more]                  next page link
*/
(() => {
  if (window.__catalogReady) return;
  window.__catalogReady = true;

  const theme = (window.theme = window.theme || {});
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const state = { abort: null, timer: null };
  const root = () => document.querySelector('catalog-root');

  const cleanup = (owner) => {
    document.body.querySelectorAll(':scope > [data-owner]').forEach((n) => {
      if (n.dataset.owner === owner) n.remove();
    });
  };

  if (!customElements.get('catalog-root')) {
    customElements.define('catalog-root', class extends HTMLElement {
      connectedCallback() {
        this.owner = this.dataset.sectionId || 'catalog';
        cleanup(this.owner);
        this.querySelectorAll('[data-drawer="filters"], [data-overlay="filters"]').forEach((el) => {
          el.dataset.owner = this.owner;
          document.body.appendChild(el);
        });
      }
      disconnectedCallback() { cleanup(this.owner); }
    });
  }

  const submitForm = (form) => {
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const swap = (doc) => {
    const open = {};
    document.querySelectorAll('details[data-group]').forEach((d) => { open[d.dataset.group] = d.open; });
    const activeId = document.activeElement && document.activeElement.id;

    new Set([...document.querySelectorAll('[data-swap]')].map((el) => el.dataset.swap)).forEach((name) => {
      const live = document.querySelectorAll(`[data-swap="${name}"]`);
      const fresh = doc.querySelectorAll(`[data-swap="${name}"]`);
      live.forEach((el, i) => { if (fresh[i]) el.replaceWith(fresh[i]); });
    });

    document.querySelectorAll('details[data-group]').forEach((d) => {
      if (d.dataset.group in open) d.open = open[d.dataset.group];
    });
    const sort = doc.querySelector('select[data-sort]');
    const liveSort = document.querySelector('select[data-sort]');
    if (sort && liveSort) liveSort.value = sort.value;
    if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
  };

  const load = async (url, { push = true } = {}) => {
    if (state.abort) state.abort.abort();
    state.abort = new AbortController();
    const r = root();
    r?.classList.add('is-loading');
    try {
      const res = await fetch(url, { signal: state.abort.signal });
      if (!res.ok) throw new Error(String(res.status));
      swap(parse(await res.text()));
      if (push) history.pushState({ catalog: true }, '', url);
      theme.initReveal?.(document);
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      r?.classList.remove('is-loading');
    }
  };

  const loadMore = async (link) => {
    link.classList.add('is-loading');
    link.setAttribute('aria-disabled', 'true');
    try {
      const res = await fetch(link.href);
      if (!res.ok) throw new Error(String(res.status));
      const doc = parse(await res.text());
      let anchor = [...document.querySelectorAll('[data-append]')].pop();
      doc.querySelectorAll('[data-append]').forEach((src) => {
        const dst = document.querySelector(`[data-append="${src.dataset.append}"]`);
        if (dst) {
          [...src.children].forEach((child) => dst.appendChild(child));
        } else if (anchor && src.children.length) {
          /* container only exists on the fetched page (e.g. page 1 had products only) -> insert it wholesale */
          anchor.after(src);
          anchor = src;
        }
      });
      const live = document.querySelector('[data-swap="pager"]');
      const fresh = doc.querySelector('[data-swap="pager"]');
      if (live && fresh) live.replaceWith(fresh);
      theme.initReveal?.(document);
    } catch (err) {
      window.location.href = link.href;
    }
  };

  document.addEventListener('click', (e) => {
    const more = e.target.closest('[data-load-more]');
    if (more) {
      e.preventDefault();
      if (more.getAttribute('aria-disabled') !== 'true') loadMore(more);
      return;
    }
    const link = e.target.closest('[data-filter-link]');
    if (link && root()) {
      e.preventDefault();
      load(link.href);
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('form[data-catalog-form]');
    if (!form || !root()) return;
    e.preventDefault();
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => { if (typeof v === 'string' && v !== '') params.append(k, v); });
    const base = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin).pathname;
    load(`${base}?${params.toString()}`);
    if (e.submitter && e.submitter.hasAttribute('data-apply')) {
      theme.closeDrawer?.('filters');
      document.querySelector('.catalog__bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  document.addEventListener('change', (e) => {
    const sort = e.target.closest && e.target.closest('select[data-sort]');
    if (sort) {
      if (sort.form) { submitForm(sort.form); return; }
      /* no filter form rendered (no storefront filters): sort via the URL */
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      if (root()) load(url.toString());
      else window.location.href = url.toString();
      return;
    }
    const check = e.target.closest && e.target.closest('.cfilter input');
    if (check && check.form && check.form.matches('[data-catalog-form]')) {
      clearTimeout(state.timer);
      state.timer = setTimeout(() => submitForm(check.form), 220);
    }
  });

  window.addEventListener('popstate', () => { if (root()) load(window.location.href, { push: false }); });
})();
